/**
 * useTelemetryTile — shared loading/error/data plumbing for super-admin
 * dashboard tiles that follow the pattern:
 *
 *   1. On mount or parameter change, GET the endpoint.
 *   2. Handle three states: loading, error (especially 403 →
 *      caller typically returns null), data.
 *   3. Guard against setState-after-unmount.
 *   4. Expose a `refetch()` for callers that mutate and need to
 *      re-pull (e.g. FailedLoginActivityTile after PUT country-allowlist).
 *
 * Before this hook, 3 tiles open-coded the same ~15-line useEffect.
 * New tiles (Digest deliverability, Slack alert firing, etc.) should
 * compose this rather than re-implement it.
 *
 * Usage:
 *   const { data, loading, error, refetch } = useTelemetryTile({
 *     url: `/analytics/glossary/heatmap?window_days=${windowDays}`,
 *     deps: [windowDays],
 *   });
 *
 *   if (error === 'forbidden') return null;
 *   if (loading) return <Skeleton />;
 *   ...
 *
 * Error classification:
 *   - HTTP 403           → 'forbidden' (caller should usually render null)
 *   - Any other failure  → 'fetch_failed'
 *
 * Intentionally **not** caching across instances — telemetry tiles
 * tend to want fresh data per render, and the auth-scoped nature of
 * these endpoints means a shared cache gets stale fast.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';


export function useTelemetryTile({ url, deps = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref so `refetch()` doesn't retrigger its own useEffect chain.
  const cancelledRef = useRef(false);

  const fetchNow = useCallback(async () => {
    if (!url) return;
    cancelledRef.current = false;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(url);
      if (!cancelledRef.current) setData(res.data);
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e?.response?.status === 403 ? 'forbidden' : 'fetch_failed');
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchNow();
    return () => {
      cancelledRef.current = true;
    };
    // The caller controls recomputation via `deps`. We intentionally
    // don't include `fetchNow` in deps — URL is already its dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refetch: fetchNow };
}
